import { useState, useEffect } from "react";
import { FileText, Download } from "lucide-react";
import { ASSETS, PARISH, PRIESTS, PARISH_CONTENT } from "@/lib/constants";
import { useContent } from "@/lib/ContentContext";
import { fetchBulletins, fetchSlides } from "@/lib/api";
import Slideshow from "@/components/Slideshow";
import SEO from "@/components/SEO";
import { ClickableImage } from "@/components/Lightbox";

const SLIDES = [
    { src: ASSETS.sanctuary, alt: "Inside the sanctuary at Holy Trinity", caption: "Inside the sanctuary at Holy Trinity" },
    { src: ASSETS.massCelebration, alt: "Celebrating the Eucharist", caption: "The Holy Mass celebrated with reverence and joy" },
    { src: ASSETS.procession, alt: "Children's procession on a Sunday", caption: "Our children, the joy of the parish, in procession" },
    { src: ASSETS.marianShrine, alt: "Marian shrine in the parish grounds", caption: "Marian shrine in the parish grounds" },
    { src: ASSETS.holyWaterFont, alt: "Holy water font at the parish entrance", caption: "Holy water font at the church entrance" },
    { src: ASSETS.steeple, alt: "Cross atop Holy Trinity steeple", caption: "The cross that crowns our parish" },
];

const TABS = [
    { id: "overview", label: "The Vision (1992)" },
    { id: "inception", label: "The Inception Process" },
    { id: "first-steps", label: "The First Steps" },
    { id: "growth", label: "Growth, Naming & Today" },
];

const PriestCard = ({ p, highlight }) => (
    <figure
        data-testid={`priest-card-${p.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
        className={`group relative overflow-hidden rounded-2xl border ${highlight ? "border-gold-300 bg-gradient-to-b from-cream-50 to-white shadow-md" : "border-maroon-100 bg-white/85"} hover:shadow-lg transition-shadow`}
    >
        {highlight && (
            <div className="absolute top-3 left-3 z-10 rounded-full bg-maroon-600 text-cream-50 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em]">
                Parish Priest
            </div>
        )}
        <div className="aspect-[4/5] overflow-hidden bg-gradient-to-b from-cream-100 to-cream-50 flex items-center justify-center">
            <ClickableImage
                src={p.image}
                alt={`${p.name}, ${p.role}, Holy Trinity Kileleshwa`}
                className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-[1.03]"
            />
        </div>
        <figcaption className="px-4 py-3 text-center">
            <div className="font-serif text-lg text-maroon-800">{p.name}</div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-gold-500/90 font-semibold">{p.role}</div>
        </figcaption>
    </figure>
);

const PriestGroup = ({ eyebrow, title, priests, highlightFirst }) => (
    <div className="mt-10">
        <div className="flex items-baseline gap-3">
            {eyebrow && <span className="text-[11px] uppercase tracking-[0.22em] text-gold-500 font-semibold">{eyebrow}</span>}
            <h3 className="font-serif text-xl sm:text-2xl text-maroon-800">{title}</h3>
        </div>
        <div className="gold-divider my-3 max-w-[160px] opacity-50" />
        <div className="grid gap-6 grid-cols-2 sm:grid-cols-3 md:grid-cols-4">
            {priests.map((p, i) => (
                <PriestCard key={p.name} p={p} highlight={highlightFirst && i === 0} />
            ))}
        </div>
    </div>
);

const About = () => {
    const { content } = useContent();
    const PC = { vision: content.vision, mission: content.mission, values: content.values };
    const [tab, setTab] = useState("overview");

    return (
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-14">
            <SEO
                title="About the Parish"
                description="About Holy Trinity Catholic Church, Kileleshwa, our story, patronage of the Holy Trinity, clergy, vision, mission and values. A Catholic parish of the Archdiocese of Nairobi on Othaya Road."
            />
            <span className="eyebrow">About Us · Archdiocese of Nairobi</span>
            <h1 className="heading-display mt-2 text-pretty">A Community of Love, Unity and Service</h1>
            <p className="mt-3 font-brand text-xl sm:text-2xl italic" style={{ color: "#C9A227" }}>
                &ldquo;Empowered and Sent&rdquo;
            </p>
            <p className="mt-4 max-w-3xl leading-relaxed text-lg" style={{ color: "#F3EDE4" }}>
                Holy Trinity Catholic Church, Kileleshwa Parish Community is a parish of the
                <span className="font-semibold" style={{ color: "#FFFFFF" }}> Archdiocese of Nairobi</span>, located on
                <span className="font-semibold" style={{ color: "#FFFFFF" }}> Othaya Road, Kileleshwa</span>. We gather around
                the Word and the Eucharist as the People of God and are sent out as missionary disciples.
            </p>

            <div className="my-10">
                <AboutSlideshow fallback={SLIDES} />
            </div>

            {/* Our Parish overview (relocated from homepage) */}
            <section
                data-testid="about-our-parish"
                className="mt-14 grid gap-10 md:grid-cols-2 items-center"
            >
                <div className="relative">
                    <div className="absolute -inset-3 stained-grid rounded-2xl" />
                    <img
                        src={ASSETS.marianShrine}
                        alt="Marian shrine at Holy Trinity"
                        className="relative rounded-2xl object-cover w-full aspect-[4/5] sm:aspect-[5/6] shadow-lg"
                        loading="lazy"
                        decoding="async"
                        width={800}
                        height={1000}
                    />
                </div>
                <div>
                    <span className="eyebrow">Our Parish</span>
                    <h2 className="heading-section mt-1">Rooted in the Holy Trinity. Walking together in Kileleshwa.</h2>
                    <p className="mt-4 text-maroon-700/90 leading-relaxed">
                        For a decade plus, Holy Trinity Catholic Parish Kileleshwa has been a spiritual home for
                        families around the Parish, Kileleshwa, Lavington and the wider Nairobi community. We
                        gather around the Eucharist Table and the Word as a community, living out the gospel
                        through worship and service.
                    </p>
                    <p className="mt-3 text-maroon-700/90 leading-relaxed">
                        Whether you are seeking to get involved in serving or simply find peace, our doors and
                        hearts are open to you.
                    </p>
                </div>
            </section>

            {/* Vision · Mission · Values (COLUS) */}
            <section data-testid="about-vmv" className="mt-14 space-y-10">
                {/* Vision */}
                <div data-testid="about-vision" className="rounded-2xl border p-6 sm:p-8" style={{ borderColor: "rgba(201,162,39,0.22)", background: "#3d2e2c" }}>
                    <span className="eyebrow">Our Vision</span>
                    <h2 className="heading-section mt-1">{PC.vision.heading}</h2>
                    <div className="gold-divider my-4 max-w-xs" />
                    <p className="text-base sm:text-lg leading-relaxed" style={{ color: "#F3EDE4" }}>
                        {PC.vision.body}
                    </p>
                </div>

                {/* Mission */}
                <div data-testid="about-mission" className="rounded-2xl border p-6 sm:p-8" style={{ borderColor: "rgba(201,162,39,0.22)", background: "#3d2e2c" }}>
                    <span className="eyebrow">Our Mission</span>
                    <h2 className="heading-section mt-1">{PC.mission.heading}</h2>
                    <div className="gold-divider my-4 max-w-xs" />
                    <p className="text-base leading-relaxed" style={{ color: "#F3EDE4" }}>
                        {PC.mission.intro}
                    </p>
                    <ul className="mt-4 space-y-2.5">
                        {PC.mission.commitments.map((c) => (
                            <li key={c} className="flex gap-3" style={{ color: "#F3EDE4" }}>
                                <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full" style={{ background: "#C9A227" }} />
                                <span>{c}</span>
                            </li>
                        ))}
                    </ul>
                    <p className="mt-5 text-base leading-relaxed" style={{ color: "#F3EDE4" }}>
                        {PC.mission.closing}
                    </p>
                    <blockquote className="mt-6 border-l-4 pl-4 italic font-serif text-lg sm:text-xl" style={{ borderColor: "#C9A227", color: "#FFFFFF" }}>
                        &ldquo;{PC.mission.pledge}&rdquo;
                    </blockquote>
                </div>

                {/* Values, COLUS */}
                <div data-testid="about-values" className="rounded-2xl border p-6 sm:p-8" style={{ borderColor: "rgba(201,162,39,0.22)", background: "#3d2e2c" }}>
                    <span className="eyebrow">Our Values</span>
                    <h2 className="heading-section mt-1">{PC.values.heading}</h2>
                    <div className="gold-divider my-4 max-w-xs" />
                    <p className="text-base leading-relaxed max-w-3xl" style={{ color: "#F3EDE4" }}>
                        {PC.values.intro}
                    </p>
                    <div className="mt-6 grid gap-5 md:grid-cols-3">
                        {PC.values.pillars.map((v, i) => (
                            <div
                                key={v.title || i}
                                data-testid={`values-pillar-${(v.title || `p${i}`).toLowerCase()}`}
                                className="relative rounded-xl p-5"
                                style={{ background: "#2e2220", border: "1px solid rgba(201,162,39,0.22)" }}
                            >
                                <div className="flex items-baseline gap-3">
                                    <span className="font-brand text-3xl font-bold" style={{ color: "#C9A227" }}>
                                        {i + 1}
                                    </span>
                                    <h3 className="font-serif text-2xl" style={{ color: "#FFFFFF" }}>{v.title}</h3>
                                </div>
                                <div className="gold-divider my-3 max-w-[80px] opacity-70" />
                                <ul className="space-y-2">
                                    {v.points.map((pt, j) => (
                                        <li key={`${i}-${j}`} className="flex gap-2 text-sm leading-relaxed" style={{ color: "#F3EDE4" }}>
                                            <span className="mt-1.5 h-1 w-1 flex-shrink-0 rounded-full" style={{ background: "#C9A227" }} />
                                            {pt}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Parish Priests */}
            <section className="mt-14">
                <span className="eyebrow">Shepherds of the Parish</span>
                <h2 className="heading-section mt-1">Our Parish Priests</h2>
                <div className="gold-divider my-4 max-w-xs opacity-60" />
                <p className="text-maroon-700/85 max-w-3xl">
                    We give thanks for the priests who have shepherded Holy Trinity Kileleshwa from its earliest
                    days under a tree in 1998, through the building of our present church, and into the community
                    we are today, the parish priest currently serving, the assistant priests at his side, and
                    those who have gone before us in this ministry.
                </p>

                <div className="mt-8 grid gap-6 grid-cols-2 sm:grid-cols-3 md:grid-cols-4">
                    <PriestCard p={PRIESTS.currentParishPriest} highlight />
                    {PRIESTS.currentlyServing.map((p) => (
                        <PriestCard key={p.name} p={p} />
                    ))}
                </div>

                <PriestGroup
                    eyebrow="Emeriti"
                    title="Former Parish Priests"
                    priests={PRIESTS.formerParishPriests}
                />

                <PriestGroup
                    eyebrow="Also gratefully remembered"
                    title="Former Serving Priests"
                    priests={PRIESTS.formerServing}
                />
            </section>

            {/* Interactive history, tabs */}
            <section className="mt-16">
                <span className="eyebrow">Our Story</span>
                <h2 className="heading-section mt-1">About the Holy Trinity Church, Kileleshwa</h2>
                <div className="gold-divider my-4 max-w-xs opacity-60" />

                <div role="tablist" className="flex flex-wrap gap-2 border-b border-maroon-100 pb-3 mb-6">
                    {TABS.map((t) => (
                        <button
                            key={t.id}
                            role="tab"
                            aria-selected={tab === t.id}
                            onClick={() => setTab(t.id)}
                            data-testid={`about-tab-${t.id}`}
                            className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                                tab === t.id
                                    ? "bg-maroon-600 text-cream-50"
                                    : "text-maroon-700 hover:bg-cream-200"
                            }`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>

                <div className="prose prose-maroon max-w-none text-maroon-800/95 leading-relaxed space-y-4 animate-fade-up">
                    {tab === "overview" && (
                        <div data-testid="about-panel-overview">
                            <p>
                                The idea of starting a church in the Kileleshwa area was mooted in <span className="font-semibold">1992</span> by the then Parish Priest of Consolata Shrine,Westlands, <span className="italic">Fr. Rossi</span>, jointly with members of the Consolata Shrine and the Kileleshwa community in the area, who were parishioners of the Consolata Shrine.
                            </p>
                            <p>
                                A church in the Kileleshwa area was befitting, as the population of the area was growing fast.
                            </p>
                            <blockquote className="border-l-4 border-gold-400 pl-4 italic text-maroon-700/90 my-6">
                                &ldquo;Just as the early Christian Church was founded on Christian communities gathering
                                regularly to pray together, to read and meditate on the Scriptures, to listen to the
                                teachings of the Apostles (Acts 2:42), so was the Holy Trinity Church, Kileleshwa.&rdquo;
                            </blockquote>
                        </div>
                    )}

                    {tab === "inception" && (
                        <div data-testid="about-panel-inception" className="space-y-4">
                            <h3 className="font-serif text-2xl text-maroon-800 !mt-0">The Inception Process</h3>
                            <p>
                                Early in 1992, after every Mass at the Consolata Shrine Church, announcements were made
                                asking people from Kileleshwa to register their names. One warm afternoon in August 1992,
                                <span className="font-semibold"> Sr. Felicina of the Little Daughters of St. Joseph</span>,
                                accompanied by Mr. Samuel Muturi (Fr. Rossi&rsquo;s cook), visited the house of Mr. and Mrs.
                                <span className="font-semibold"> Frederick Makoriwa</span> on Olekejuado Road, Kileleshwa,
                                to enquire about the Catholics in the area.
                            </p>
                            <p>
                                Mrs. Makoriwa took them to the houses of Mr. and Mrs. Leonard Amalia, Mr. and Mrs. Bernard
                                Adundo, and Mr. and Mrs. Sylvano Odero, all on Gichugu Road. The following Sunday, after
                                each Mass at the Shrine, it was announced that there would be a meeting of the Catholics
                                in Kileleshwa.
                            </p>
                            <ul className="list-disc list-disc-gold pl-6 space-y-2 marker:text-gold-500">
                                <li>Roselyne, a teacher at Kileleshwa Primary School, offered her house for the first meeting.</li>
                                <li>Second meeting: at the Makoriwas&rsquo;. Third meeting: at the Amalias&rsquo;.</li>
                                <li>Meetings were held every Sunday at 2 pm, and the Holy Rosary was recited each time.</li>
                                <li>The group had no leader; members volunteered to host. Fr. Rossi, Sr. Wilma and Sr. Felicina visited often.</li>
                            </ul>
                            <p>
                                After about a year, Fr. Rossi conducted elections: <span className="font-semibold">Mr. Charles Otieno</span> was elected Chairman and <span className="font-semibold">Mrs. Amalia</span> the treasurer. The meeting day changed to Wednesday at 6:30 pm, and members began reading the Gospel instead of praying the Rosary. About a year later, the group was named <span className="font-semibold">Our Lady of Mt. Carmel Small Christian Community</span>.
                            </p>
                            <p>
                                Mr. Otieno stepped down in November 1997 and <span className="font-semibold">Mrs. Makoriwa</span> was elected in absentia, the title changed from Chairman to Coordinator. Sisters Pierina and Carla of the Comboni Sisters attended the meetings. The community was also visited twice by <span className="italic">Fr. Joseph Heeley</span> from Tanzania and Rose Msimba of Holy Trinity Buruburu, together with a member from Kisumu Diocese, to help members grow in SCC. Fr. Heeley donated books.
                            </p>
                        </div>
                    )}

                    {tab === "first-steps" && (
                        <div data-testid="about-panel-first-steps" className="space-y-4">
                            <h3 className="font-serif text-2xl text-maroon-800 !mt-0">The First Steps</h3>
                            <p>
                                Other parish priests coming thereafter, namely
                                <span className="font-semibold"> Fr. Canzian, Fr. Cellana and Fr. Marino</span>, had a
                                similar commitment to the Church project. Their determination was to give the most
                                beautiful gift to the Holy Trinity during the Great Year of the Jubilee.
                            </p>
                            <p>Some of the important steps taken were:</p>
                            <ul className="list-disc list-disc-gold pl-6 space-y-2 marker:text-gold-500">
                                <li>Formation of the 1st SCC as a basis upon which the Church would be founded. After Our Lady of Mt. Carmel SCC, the second to be formed was <span className="font-semibold">St. Lawrence</span>, followed by <span className="font-semibold">St. Monica</span> and <span className="font-semibold">Emmanuel</span> SCCs.</li>
                                <li>Purchase of the two plots upon which the Church now stands.</li>
                                <li>Charging the Development Committee of Consolata Church with overseeing the construction of the Church through mobilisation of resources (mainly financial).</li>
                            </ul>
                            <p>
                                In <span className="font-semibold">1998</span>, the first Mass was celebrated under a tree
                                after the blessing of the grounds. After a while, the old house on the grounds was
                                renovated and became the church for about 100 people. As the congregation grew larger, a
                                tent was pitched on the grounds and used as the church until the new, larger church was
                                completed, officially opened by
                                <span className="font-semibold"> His Grace Archbishop Ndingi Mwana a&rsquo; Nzeki in December 2000</span>.
                            </p>
                            <p className="italic text-maroon-700/90">
                                It is from these humble beginnings that the Holy Trinity Kileleshwa community grew in
                                unity and appreciation of each other.
                            </p>
                        </div>
                    )}

                    {tab === "growth" && (
                        <div data-testid="about-panel-growth" className="space-y-4">
                            <h3 className="font-serif text-2xl text-maroon-800 !mt-0">Community Participation</h3>
                            <p>
                                Because of the total commitment of the parishioners to the project and their immeasurable
                                generosity through financial and other material contributions, the project which looked
                                a mammoth task was completed in only a span of <span className="font-semibold">three years</span>.
                            </p>

                            <h3 className="font-serif text-2xl text-maroon-800">Naming of the Church</h3>
                            <p>
                                The Church was dedicated to the Holy Trinity in recognition of the fact that all these
                                activities were done during the three years the then Holy Father St. John Paul II had
                                dedicated to <span className="font-semibold">God the Father (1997)</span>,
                                <span className="font-semibold"> God the Son (1998)</span> and
                                <span className="font-semibold"> God the Holy Spirit (1999)</span>. A supportive devotion
                                of <span className="italic">Our Lady of Assumption</span> to the Holy Trinity was also
                                adopted after committing the choice of the devotion to prayer.
                            </p>

                            <h3 className="font-serif text-2xl text-maroon-800">A Community of Unity and Love</h3>
                            <p>
                                The first community of the Holy Trinity Church had a lot of similarities to the first
                                Christians, they were united heart and soul. This unity is still evident today. It is a
                                community of faith, worship and love.
                            </p>

                            <h3 className="font-serif text-2xl text-maroon-800">Animation of the Liturgy</h3>
                            <p>
                                The Church started with a handful of about <span className="font-semibold">100 people</span>
                                and now has a congregation of over <span className="font-semibold">700 people</span> who
                                gather each Sunday to worship the Lord through the Liturgy. Over the years the parish has
                                formed various Small Christian Communities. Other apostolic groups include the
                                <span className="font-semibold"> Third Phase</span>, the
                                <span className="font-semibold"> Youth Group</span>, the
                                <span className="font-semibold"> Children&rsquo;s Club</span> and the
                                <span className="font-semibold"> Choir</span>. They take turns in preparing, animating and
                                leading the Sunday Liturgy, special feasts and novenas.
                            </p>
                            <blockquote className="border-l-4 border-gold-400 pl-4 italic text-maroon-700/90 my-6">
                                &ldquo;The Holy Trinity Church in Kileleshwa is an example of how to put Christ&rsquo;s idea of a
                                Church in practice, a community of communities, active in participating in the destiny
                                of their Church.&rdquo;
                            </blockquote>
                        </div>
                    )}
                </div>
            </section>

            {/* Three pillars */}
            <div className="mt-16 grid gap-5 md:grid-cols-3">
                {[
                    {
                        title: "Our Worship",
                        body: "We celebrate the Holy Mass daily, with reverence for the liturgy and openness to the breath of the Holy Spirit. Confessions, adoration and devotionals enrich our parish life.",
                    },
                    {
                        title: "Our Formation",
                        body: "From PMC to YSC, Sunday School and Young Catholic Adults, we accompany every age in growing into the fullness of Christ.",
                    },
                    {
                        title: "Our Service",
                        body: "Through CWA, CJPD, Couples for Christ and our 22 Small Christian Communities, we serve our neighbours and seek justice and peace.",
                    },
                ].map((b) => (
                    <div key={b.title} className="rounded-2xl border border-maroon-100 bg-cream-50 p-5">
                        <h3 className="font-serif text-xl text-maroon-800">{b.title}</h3>
                        <div className="gold-divider my-2 opacity-50" />
                        <p className="text-sm text-maroon-700/85 leading-relaxed">{b.body}</p>
                    </div>
                ))}
            </div>

            <div className="mt-12 rounded-2xl border border-maroon-100 bg-white/70 p-6 hidden">
                {/* "Where to find us" block removed per parish feedback (Delivery 6). */}
            </div>

            <BulletinsSection />
        </div>
    );
};

/* -------- About slideshow (admin-editable via "About Page" context) -------- */
const AboutSlideshow = ({ fallback }) => {
    const [slides, setSlides] = useState(fallback);
    useEffect(() => {
        let alive = true;
        fetchSlides("about")
            .then((d) => { if (alive && d && d.length > 0) setSlides(d.map((s) => ({ src: s.url, alt: s.caption || "" }))); })
            .catch(() => { /* stay on fallback */ });
        return () => { alive = false; };
    }, []);
    return <Slideshow slides={slides} />;
};

/* -------- Church Bulletins & Magazines -------- */
const BulletinsSection = () => {
    const [bulletins, setBulletins] = useState([]);
    const [loading, setLoading]     = useState(true);

    useEffect(() => {
        let alive = true;
        fetchBulletins()
            .then((data) => alive && setBulletins(data))
            .catch(() => { /* silently fall back to empty */ })
            .finally(() => alive && setLoading(false));
        return () => { alive = false; };
    }, []);

    if (!loading && bulletins.length === 0) return null;

    return (
        <section data-testid="about-bulletins" className="mt-16">
            <span className="eyebrow">Archive</span>
            <h2 className="font-serif text-3xl text-maroon-800 mt-1">Church Bulletins &amp; Magazines</h2>
            <p className="mt-2 text-maroon-700/80 max-w-2xl">
                Browse editions of our parish newsletter, a snapshot of parish life, formation and mission through the years.
            </p>
            <div className="gold-divider my-6 opacity-60" />
            {loading ? (
                <p className="text-sm text-maroon-700/70 italic">Loading bulletins…</p>
            ) : (
                <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                    {bulletins.map((b) => (
                        <a
                            key={b.id}
                            href={b.url}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`bulletin-card-${b.id}`}
                            className="group relative overflow-hidden rounded-2xl border border-gold-300/40 bg-gradient-to-br from-maroon-700 to-maroon-900 p-5 text-cream-50 shadow-md ring-1 ring-transparent hover:ring-gold-300 hover:shadow-xl hover:-translate-y-1 transition-all"
                        >
                            <div className="absolute -right-6 -top-6 h-24 w-24 rounded-full bg-gold-300/10 blur-2xl" />
                            <div className="relative flex items-start gap-3">
                                <div className="inline-flex h-12 w-12 items-center justify-center rounded-lg border border-gold-300/60 bg-maroon-900/70 text-gold-300 group-hover:bg-gold-300 group-hover:text-maroon-900 transition-colors">
                                    <FileText size={22} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h3 className="relative font-serif text-lg text-cream-50 leading-snug">{b.title}</h3>
                                    <p className="relative text-xs text-gold-300 mt-1 font-semibold tracking-wider uppercase">{b.year}</p>
                                </div>
                            </div>
                            <div className="relative mt-4 flex items-center justify-between text-xs">
                                <span className="text-cream-50/70">
                                    {b.size_bytes ? Math.round(b.size_bytes / 1024) + " KB · PDF" : "PDF"}
                                </span>
                                <span className="inline-flex items-center gap-1 font-semibold uppercase tracking-wider text-gold-300 group-hover:text-gold-200">
                                    Open <Download size={12} />
                                </span>
                            </div>
                        </a>
                    ))}
                </div>
            )}
        </section>
    );
};

export default About;
